import type { MatchState } from '../sim/state';
import type { SimConfig } from '../config/simConfig';
import type { EntityId, TeamIdx, Vec2 } from '../types';
import { formationTargets, type TeamPhase } from './formations';
import { inferReceiver } from '../sim/receiver';
import { interceptFlight } from '../sim/aerial';
import { IDENTITY_PROFILE, type DifficultyProfile } from './difficulty';

// Team plan (§8.1), recomputed every AI PLAN_PERIOD ticks: the chaser (nearest outfield player to the
// ball), a couple of support players, per-entity formation targets, and the team phase. Pure.

export interface TeamPlan {
  chaser: EntityId | null;
  support: EntityId[];
  formationTargets: Map<EntityId, Vec2>;
  phase: TeamPhase;
  /** D39: our pass in flight → the inferred receiver sprints to THIS intercept point (else null).
   *  D111: for a ball IN THE AIR the point comes from `interceptFlight` (the real 3-D flight, bounces
   *  included) and `z` carries the ball height at the meeting — which is how the receiver knows whether
   *  he is trapping it or heading it. `z` is absent for the ground march. */
  receiver: { id: EntityId; point: Vec2; z?: number } | null;
  /** D42: HUMAN-controlled entities on this team — bots play FOR them (server-side knowledge; the sim
   *  itself never sees controller identity, only the AI layer does). */
  humans: ReadonlySet<EntityId>;
  /** D46 Items 3+4: this team's difficulty profile (AI-layer only; IDENTITY = today's tuning). Optional
   *  so a hand-built TeamPlan (tests) omits it and consumers fall back to IDENTITY_PROFILE. */
  profile?: DifficultyProfile;
}

export function planTeam(
  state: MatchState,
  team: TeamIdx,
  cfg: SimConfig,
  humans: ReadonlySet<EntityId> = new Set(),
  profile: DifficultyProfile = IDENTITY_PROFILE,
): TeamPlan {
  const size = state.mode.teamSize;
  const ball = state.ball;
  const possTeam = ball.possessor !== null ? (state.players[ball.possessor]?.team ?? null) : null;
  const phase: TeamPhase = possTeam === team ? 'ATTACK' : possTeam !== null ? 'DEFEND' : 'TRANSITION';

  // chaser = the nearest outfield player to the ball; support = the next two nearest
  const ranked: Array<{ id: EntityId; d: number }> = [];
  for (let slot = 0; slot < size; slot++) {
    const id = (team * size + slot) as EntityId;
    const p = state.players[id];
    if (!p || p.sentOff || p.isGk) continue;
    ranked.push({ id, d: Math.hypot(p.pos.x - ball.pos.x, p.pos.y - ball.pos.y) });
  }
  ranked.sort((a, b) => a.d - b.d);
  const chaser = ranked.length ? ranked[0]!.id : null;
  const support = ranked.slice(1, 3).map((r) => r.id);

  // D39: when OUR pass is in flight, designate its inferred receiver — he runs to meet the ball
  // instead of watching it get picked off (the single biggest "passing is impossible" cause).
  let receiver: TeamPlan['receiver'] = null;
  const speed = Math.hypot(ball.vel.x, ball.vel.y);
  // D111: a ball in the AIR is not its own shadow. `inferReceiver` marches along the ground, so a chip
  // or a cross designated whoever happened to stand under the arc's midpoint — and he then jogged to a
  // point the ball flew straight over. Airborne balls designate through `interceptFlight` instead: the
  // real §5.3 flight, bounce continuation and all, earliest reachable meeting wins.
  const airborne = ball.pos.z > cfg.possession.CLAIM_Z || ball.vel.z > 0.5;
  if (ball.possessor === null && ball.lastTouch?.team === team && airborne) {
    let best: { id: EntityId; point: Vec2; z: number; tick: number } | null = null;
    for (const t of state.players) {
      if (t.team !== team || t.sentOff || t.id === ball.lastTouch.entity) continue;
      // `arming: true` — a head-height meeting IS playable to a receiver who intends to head it, and
      // the height is reported so `actPlayer` can decide between the trap and the strike.
      const hit = interceptFlight(ball, t, cfg, state.conditions, { arming: true });
      if (hit && (best === null || hit.tick < best.tick || (hit.tick === best.tick && t.id < best.id))) {
        best = { id: t.id, point: hit.point, z: hit.z, tick: hit.tick };
      }
    }
    if (best) receiver = { id: best.id, point: best.point, z: best.z };
  } else if (ball.possessor === null && ball.lastTouch?.team === team && speed > 4) {
    const guess = inferReceiver(state, ball.lastTouch.entity, { x: ball.pos.x, y: ball.pos.y }, Math.atan2(ball.vel.y, ball.vel.x), speed, cfg);
    if (guess) receiver = { id: guess.id, point: guess.point };
  }

  return { chaser, support, formationTargets: formationTargets(state, team, phase, cfg), phase, receiver, humans, profile };
}
